import React, { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { selectSignedIn, signOut } from "reducers/auth";
import { AccessElement, useLoadScopes } from "helpers/auth";
import { Link, WindowLocation } from "@reach/router";
import Mobile_02 from "assets/Icon_Mobile_02.png";
import { useOnClickOutside } from "helpers/CustomUseOnClickOutside";
import {
  selectPendingGuaranteesCount,
  setPendingGuaranteesCount,
} from "reducers/guarantees";
import {
  completeUIUpdate,
  NotificationIdentifier,
  selectUIUpdate,
} from "reducers/notificationReducer";
import { endpoints } from "config";
import instance from "api";

type Props = {
  mobileNavOpen: boolean;
  closeMobileNav(): void;
  location: WindowLocation;
  hide?: boolean;
};

const Navigation: React.FC<Props> = ({
  closeMobileNav,
  location,
  mobileNavOpen,
  hide,
}) => {
  const isSignedIn = useSelector(selectSignedIn);
  const accessElements = useLoadScopes();

  const ref = useOnClickOutside(() => mobileNavOpen && closeMobileNav());

  if (!isSignedIn || (hide && !mobileNavOpen)) return null;

  const pathname = location.pathname;

  const accessGeneralElements = accessElements.filter(
    i => i.usageType === "General"
  );

  const accessManagementElements = accessElements.filter(
    i => i.usageType === "User Management"
  );

  return (
    <>
      <div
        className={`MobileNavBackground ${mobileNavOpen ? "" : "Hidden"}`}
      ></div>
      <nav
        id="MainNavigation"
        className={mobileNavOpen ? "Navigation Open" : "Navigation"}
        ref={ref}
      >
        <div className="Inner">
          <div className="Block">
            {accessGeneralElements.length > 0 && (
              <>
                <span>General</span>
                <ul className="GeneralItems">
                  {accessGeneralElements
                    // .sort((a, b) => a.displayName.localeCompare(b.displayName))
                    .map(e => (
                      <TopLevelItem
                        key={e.displayName}
                        accessElement={e}
                        active={pathname.includes(e.path)}
                        closeMobileNav={closeMobileNav}
                      />
                    ))}
                </ul>
                <div className="LineBreak" />
              </>
            )}
            {accessManagementElements.length > 0 && (
              <>
                <span>User management</span>
                <ul className="ManagerItems">
                  {accessManagementElements
                    // .sort((a, b) => a.displayName.localeCompare(b.displayName))
                    .map(e => (
                      <TopLevelItem
                        key={e.displayName}
                        accessElement={e}
                        active={pathname.includes(e.path)}
                        closeMobileNav={closeMobileNav}
                      />
                    ))}
                </ul>
              </>
            )}
          </div>
        </div>
      </nav>
    </>
  );
};

export default Navigation;

const TopLevelItem: React.FC<{
  accessElement: AccessElement;
  active: boolean;
  closeMobileNav(): void;
}> = ({ accessElement, active, closeMobileNav }) => {
  if (accessElement.path === "pendingguarantees")
    return (
      <PendingGuaranteesItem
        accessElement={accessElement}
        active={active}
        closeMobileNav={closeMobileNav}
      />
    );
  return (
    <li className={active ? "Active" : ""}>
      <Link to={accessElement.path} onClick={closeMobileNav}>
        {accessElement.displayName}
      </Link>
    </li>
  );
};

const PendingGuaranteesItem: React.FC<{
  accessElement: AccessElement;
  active: boolean;
  closeMobileNav(): void;
}> = ({ accessElement, active, closeMobileNav }) => {
  const pendingGuaranteesCount = useSelector(selectPendingGuaranteesCount);
  const uiUpdate = useSelector(selectUIUpdate);
  const dispatch = useDispatch();

  const getPendingGuaranteesCount = async () => {
    try {
      const result = await instance.get(
        endpoints.guaranteesmodule.pendingGuaranteesCount
      );

      if (
        result?.data?.status === "1" &&
        result?.data?.details?.numberOfPendingGuarantees
      ) {
        dispatch(
          setPendingGuaranteesCount(
            result.data.details.numberOfPendingGuarantees ?? null
          )
        );
      }
    } catch (error) {
      console.log(
        "Error - Failed to get the Number of pending guarantees: ",
        error
      );
    }
  };

  useEffect(() => {
    getPendingGuaranteesCount();
  }, []);

  useEffect(() => {
    if (uiUpdate?.pushType === NotificationIdentifier.NEW_PENDING_GUARANTEE) {
      getPendingGuaranteesCount();

      dispatch(completeUIUpdate());
    }
  }, [uiUpdate]);

  return (
    <li className={active ? "Active" : ""}>
      <Link to={accessElement.path} onClick={closeMobileNav}>
        {`${accessElement.displayName} (${
          typeof pendingGuaranteesCount === "number"
            ? pendingGuaranteesCount
            : "-"
        })`}
      </Link>
    </li>
  );
};
